import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import ModalResult from "../ModalResult";
import PriceList from "../PriceList";
import BillingDetailsForm from "./BillingDetailsForm";

import { AppContext } from "../../context/AppContext";
import axiosInstance from "../../api/AxiosConfig";

const ModalPriceList = () => {
  const { t, i18n } = useTranslation();

  const {
    showModalCredit,
    setShowModalCredit,
    showAlert,
    modalPriceListSelectedPlan,
    setModalPriceListSelectedPlan,
  } = useContext(AppContext);

  // step control
  const [selectedPlan, setSelectedPlan] = useState(null);

  // If a preselected plan exists in context when opening, go straight to billing step
  React.useEffect(() => {
    if (showModalCredit && modalPriceListSelectedPlan) {
      setSelectedPlan(modalPriceListSelectedPlan);
    }
  }, [showModalCredit, modalPriceListSelectedPlan]);

  const onClose = () => {
    setShowModalCredit(false);
    setSelectedPlan(null);
    setModalPriceListSelectedPlan(null);
  };

  const handleLoadError = () => {
    // Zavřít modal a zobrazit chybovou hlášku přes SimpleAlert (globální pattern)
    setShowModalCredit(false);
    if (typeof showAlert === "function") {
      showAlert(
        "Nepodařilo se načíst ceník. Zkuste to prosím znovu.",
        "danger"
      );
    }
  };

  if (!showModalCredit) {
    return null;
  }

  const handleChoosePlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleBillingSubmit = async (payload) => {
    try {
      // 1) Optional: save billing details if backend supports (left as future endpoint)
      // await axiosInstance.post('billing-details/', payload);

      // 2) Create payment for selected plan and redirect
      const planSlug = payload?.selectedPlan?.slug;
      if (!planSlug) throw new Error("Missing plan slug");
      // Build request payload from billing form values (send only filled fields)
      const isB2C = payload?.customerType === "b2c";
      const fullName = isB2C
        ? `${payload?.first_name || ""} ${payload?.last_name || ""}`.trim()
        : undefined;

      // Map our locale-style codes (cs, sk, ...) to gateway codes (CZ, SK, ...)
      const mapGatewayCountry = (code) => {
        if (!code) return undefined;
        const lc = String(code).toLowerCase();
        const map = {
          cs: "CZ",
          sk: "SK",
          hu: "HU",
          pl: "PL",
          ro: "RO",
          de: "DE",
          all: "ALL",
          us: "US",
        };
        return map[lc] || String(code).toUpperCase();
      };

      const countryCode = mapGatewayCountry(
        payload?.state || payload?.country_billing
      );

      const requestBody = {
        planSlug,
        // common (always include to satisfy backend "required" fields)
        email: String(payload?.email ?? "").trim(),
        phone: String(payload?.phone ?? "").trim(),
        country: countryCode,
        lang: i18n?.language || undefined,
        // B2C specific
        ...(isB2C && fullName ? { fullName } : {}),
        // B2B specific
        ...(!isB2C
          ? {
              fullName: payload?.name || undefined, // company name
              billingAddrCity: payload?.city || undefined,
              billingAddrStreet: payload?.street || undefined,
              billingAddrPostalCode: payload?.postal_code || undefined,
              billingAddrCountry: countryCode,
              ico: payload?.ico || undefined,
              dic: payload?.dic || undefined,
            }
          : {}),
         url_paid: `${window.location.origin}/cs/tools/heatmaps/?p_status=success`,
      };

      // Remove undefined or empty string values to avoid sending blank keys
      Object.keys(requestBody).forEach((k) => {
        if (
          requestBody[k] === undefined ||
          (typeof requestBody[k] === "string" && requestBody[k].trim() === "")
        ) {
          delete requestBody[k];
        }
      });

      const { data } = await axiosInstance.post(
        `payments/create/`,
        requestBody
      );
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        throw new Error("Missing redirectUrl in response");
      }
    } catch (e) {
      console.error(e);
      showAlert?.(
        t("companyDetailsForm.errorMessages.updateFailedErrorMsg"),
        "danger"
      );
    }
  };

  return (
    <ModalResult
      onClose={onClose}
      description={
        selectedPlan ? (
          <BillingDetailsForm
            selectedPlan={selectedPlan}
            onCancel={() => {
              setSelectedPlan(null);
              setModalPriceListSelectedPlan(null);
            }}
            onSubmit={handleBillingSubmit}
          />
        ) : (
          <PriceList
            notLoggedClick={onClose}
            onLoadError={handleLoadError}
            onChoosePlan={handleChoosePlan}
          />
        )
      }
      contentOutsideContainer={true}
      showCloseButton={true}
    />
  );
};

export default ModalPriceList;
